import type { ReactNode } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import type { Security } from '../../types/Security';
import { Box, Flex, type BoxProps } from '../Core';
import { CurrencyIcon } from '../CurrencyIcon';
import { EllipsisBoxWithTooltip } from '../Form/BaseSelect';
import { MarketLogo } from '../MarketLogos';
import { resolveSecurityIconCurrency } from './resolveSecurityIconCurrency';
import { Wrapper } from './styles';

type SecurityProps = (
  | {
      symbol?: string;
      security: Security;
    }
  | {
      symbol: string;
      security?: Security;
    }
) & {
  currency?: string;
  /** Show this venue's logo in place of the currency icon. */
  marketLogo?: string;
  showIcon?: boolean;
  showDescription?: boolean;
  theme?: DefaultTheme;
  iconSize?: number;
  colorful?: boolean;
  unifiedLiquidityIndicator?: ReactNode;
  suffix?: ReactNode;
} & Omit<BoxProps, 'security'>;

export function SecurityRenderer({
  symbol,
  security,
  currency,
  marketLogo,
  showIcon,
  showDescription,
  theme,
  iconSize = 16,
  colorful,
  unifiedLiquidityIndicator,
  suffix,
  ...props
}: SecurityProps) {
  const hookTheme = useTheme();
  const themeToUse = theme ?? hookTheme;
  const { colorTextSubtle, colorTextImportant } = themeToUse;

  const displaySymbol = security?.DisplaySymbol || symbol;
  const iconCurrency = resolveSecurityIconCurrency({ security, currency, symbol });
  return (
    <Wrapper gap="spacing4" color="colorTextImportant" {...props}>
      {showIcon !== false &&
        (currency != null || security != null) &&
        (marketLogo != null ? (
          <MarketLogo market={marketLogo} title={marketLogo} size={iconSize} style={ICON_STYLE} />
        ) : (
          <CurrencyIcon
            size={iconSize}
            currency={iconCurrency}
            theme={themeToUse}
            colorful={colorful}
            style={ICON_STYLE}
          />
        ))}

      <Box overflow="hidden" flex="1 1 0" textAlign="left">
        <Flex color={colorTextImportant}>
          <EllipsisBoxWithTooltip
            data-testid="security-renderer-symbol"
            tooltipProps={{
              placement: 'top',
              tooltip: displaySymbol,
            }}
          >
            {displaySymbol}
          </EllipsisBoxWithTooltip>
          {unifiedLiquidityIndicator}
          {suffix && <Box ml="spacing8">{suffix}</Box>}
        </Flex>
        {showDescription && (
          <Flex
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            w="100%"
            color={colorTextSubtle}
            fontSize="fontSizeTiny"
            gap="spacing4"
            data-testid="security-renderer-description"
          >
            {security?.Description}
          </Flex>
        )}
      </Box>
    </Wrapper>
  );
}

const ICON_STYLE = {
  flexShrink: 0,
};
